import React, {useState} from 'react'
import EventPage from 'organization/Event/EventPage'
import BreadcrumMenu from 'organization/theme/Breadcrum'

import Step1Upload from './Step1Upload'
import Step2SelectColumn from './Step2SelectColumn'
import Step3PreviewSend from './Step3PreviewSend'

import styled from 'styled-components'
import {CsvRow} from './types'
import {useCsvImport} from 'organization/Event/Integrations/CsvImport/CsvImportProvider'
import {ImportHistory} from 'organization/Event/Integrations/CsvImport/ImportHistory'
import {InfoSection} from 'organization/Event/Integrations/CsvImport/InfoSection'
import {SendModal} from 'organization/Event/Integrations/CsvImport/SendModal'
import {EventFaqHelp} from 'organization/Event/Help/FAQ'
import {useI18n} from 'i18n/I18nContext'

export const CsvImport = () => {
  const {t} = useI18n()
  const {imports} = useCsvImport()
  const [step, setStep] = useState<number>(1)

  const [file, setFile] = useState<File | null>(null)
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<CsvRow[]>([])
  const [selectedEmailColumn, setSelectedEmailColumn] = useState<string>('')

  // modal-related state
  const [showModal, setShowModal] = useState<boolean>(false)
  const [pendingEmails, setPendingEmails] = useState<string[]>([])
  const [pendingFileName, setPendingFileName] = useState<string>('')

  const [totalUniqueValidEmails, setTotalUniqueValidEmails] =
    useState<number>(0)

  const toggleShowModal = () => setShowModal((curr) => !curr)

  const handleRequestSend = (emails: string[], fileName: string) => {
    setPendingEmails(emails)
    setPendingFileName(fileName)
    toggleShowModal()
  }

  return (
    <EventPage
      modals={
        <SendModal
          show={showModal}
          toggle={toggleShowModal}
          emails={pendingEmails}
          fileName={pendingFileName}
          onFinish={() => setStep(1)}
          totalEmails={totalUniqueValidEmails}
        />
      }
    >
      <Container>
        <BreadcrumMenu
          title={t('orgCsv.breadcrumb')}
          helpInfo={<EventFaqHelp faqKey="csvIntegration" />}
        />

        <FileProcessHolder>
          <h4>{t('orgCsv.uploadTitle')}</h4> <br />
          <StepIndicator>
            <Step active={step === 1} onClick={() => setStep(1)}>
              {t('orgCsv.step1')}
            </Step>
            <Step active={step === 2} onClick={() => step > 1 && setStep(2)}>
              {t('orgCsv.step2')}
            </Step>
            <Step active={step === 3} onClick={() => step > 2 && setStep(3)}>
              {t('orgCsv.step3')}
            </Step>
          </StepIndicator>
          {step === 1 && (
            <Step1Upload
              file={file}
              setFile={setFile}
              setHeaders={setHeaders}
              setRows={setRows}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <Step2SelectColumn
              headers={headers}
              rows={rows}
              selectedEmailColumn={selectedEmailColumn}
              setSelectedEmailColumn={setSelectedEmailColumn}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <Step3PreviewSend
              rows={rows}
              selectedEmailColumn={selectedEmailColumn}
              onBack={() => setStep(2)}
              file={file}
              onRequestSend={handleRequestSend}
              setTotalUniqueValidEmails={setTotalUniqueValidEmails}
            />
          )}
        </FileProcessHolder>

        {step === 1 && (
          <>
            <ImportHistory imports={imports} />
            <InfoSection />
          </>
        )}
      </Container>
    </EventPage>
  )
}

const Container = styled.div`
  padding: 20px 30px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const StepIndicator = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`

const Step = styled.div<{active?: boolean}>`
  flex: 1;
  text-align: center;
  padding: 10px 0;
  font-weight: 600;
  border-radius: 8px;
  color: ${({active}) => (active ? '#ffffff' : '#5f6368')};
  background: ${({active}) => (active ? 'var(--clr-gradient-14)' : '#f1f3f4')};
  cursor: pointer;
  transition: all 0.2s;
`

const FileProcessHolder = styled.div`
  background: #f8f9fa;
  border-radius: 5px;
  padding: 20px;
`
